import { fetchHeatPumps } from '../../services/heatPumpService';
import { supabaseClient } from '../../services/supabase';

export type HeatPumpPerCapitaFacts = {
  comparisonYear: number | null;
  localUnits: number | null;
  localPopulation: number | null;
  localPer10k: number | null;
  californiaUnits: number | null;
  californiaPopulation: number | null;
  californiaPer10k: number | null;
  percentDifferenceVsCalifornia: number | null;
  comparatorAvailable: boolean;
};

export type HeatPumpCostFacts = {
  comparisonYear: number | null;
  localHvacProjectCost: number | null;
  localHpwhProjectCost: number | null;
  californiaHvacProjectCost: number | null;
  californiaHpwhProjectCost: number | null;
  selectedTechnology: 'HVAC' | 'HPWH';
  localSelectedAverage: number | null;
  californiaSelectedAverage: number | null;
};

export function aggregateUnitsByYear(
  records: { year: number; units_installed?: number | null }[] | null | undefined
): Map<number, number> {
  const map = new Map<number, number>();
  records?.forEach((record) => {
    const year = record.year;
    const units = record.units_installed;
    if (units == null || !Number.isFinite(units)) return;
    map.set(year, (map.get(year) || 0) + units);
  });
  return map;
}

function populationMap(
  records: { year: number; population?: number | null }[] | null | undefined
): Map<number, number> {
  const map = new Map<number, number>();
  records?.forEach((record) => {
    if (record.population != null && record.population > 0) {
      map.set(record.year, record.population);
    }
  });
  return map;
}

/** Latest year where both geographies have TECH units and population (same denominator definition). */
export function latestCommonCompleteTechPerCapitaYear(params: {
  localUnitsByYear: Map<number, number>;
  californiaUnitsByYear: Map<number, number>;
  localPopByYear: Map<number, number>;
  californiaPopByYear: Map<number, number>;
  maxYear?: number;
}): number | null {
  const cap = params.maxYear ?? new Date().getFullYear();
  const localYears = [...params.localUnitsByYear.keys()].filter(
    (y) => y <= cap && params.localPopByYear.has(y) && (params.localUnitsByYear.get(y) ?? 0) >= 0
  );
  const caYears = new Set(
    [...params.californiaUnitsByYear.keys()].filter(
      (y) => y <= cap && params.californiaPopByYear.has(y)
    )
  );
  const eligible = localYears.filter((y) => caYears.has(y));
  if (!eligible.length) return null;
  return Math.max(...eligible);
}

export function buildHeatPumpPerCapitaFactsFromMaps(params: {
  localUnitsByYear: Map<number, number>;
  californiaUnitsByYear: Map<number, number>;
  localPopByYear: Map<number, number>;
  californiaPopByYear: Map<number, number>;
}): HeatPumpPerCapitaFacts {
  const comparisonYear = latestCommonCompleteTechPerCapitaYear(params);
  if (comparisonYear == null) {
    return {
      comparisonYear: null,
      localUnits: null,
      localPopulation: null,
      localPer10k: null,
      californiaUnits: null,
      californiaPopulation: null,
      californiaPer10k: null,
      percentDifferenceVsCalifornia: null,
      comparatorAvailable: false,
    };
  }

  const localUnits = params.localUnitsByYear.get(comparisonYear) ?? null;
  const californiaUnits = params.californiaUnitsByYear.get(comparisonYear) ?? null;
  const localPopulation = params.localPopByYear.get(comparisonYear) ?? null;
  const californiaPopulation = params.californiaPopByYear.get(comparisonYear) ?? null;

  const localPer10k =
    localPopulation && localPopulation > 0 && localUnits != null
      ? (localUnits / localPopulation) * 10000
      : null;
  const californiaPer10k =
    californiaPopulation && californiaPopulation > 0 && californiaUnits != null
      ? (californiaUnits / californiaPopulation) * 10000
      : null;

  let percentDifferenceVsCalifornia: number | null = null;
  if (localPer10k != null && californiaPer10k != null && californiaPer10k > 0) {
    percentDifferenceVsCalifornia = ((localPer10k - californiaPer10k) / californiaPer10k) * 100;
  }

  return {
    comparisonYear,
    localUnits,
    localPopulation,
    localPer10k,
    californiaUnits,
    californiaPopulation,
    californiaPer10k,
    percentDifferenceVsCalifornia,
    comparatorAvailable: localPer10k != null && californiaPer10k != null,
  };
}

export async function fetchHeatPumpPerCapitaFacts(geofips: number): Promise<HeatPumpPerCapitaFacts> {
  const [locationData, californiaData, locationPop, californiaPop] = await Promise.all([
    fetchHeatPumps({ select: 'year, units_installed', geofips }),
    fetchHeatPumps({ select: 'year, units_installed', geofips: 6 }),
    supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', geofips)
      .order('year', { ascending: true }),
    supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', 6)
      .order('year', { ascending: true }),
  ]);

  if (locationData.error || californiaData.error || locationPop.error || californiaPop.error) {
    return buildHeatPumpPerCapitaFactsFromMaps({
      localUnitsByYear: new Map(),
      californiaUnitsByYear: new Map(),
      localPopByYear: new Map(),
      californiaPopByYear: new Map(),
    });
  }

  return buildHeatPumpPerCapitaFactsFromMaps({
    localUnitsByYear: aggregateUnitsByYear(locationData.data),
    californiaUnitsByYear: aggregateUnitsByYear(californiaData.data),
    localPopByYear: populationMap(locationPop.data),
    californiaPopByYear: populationMap(californiaPop.data),
  });
}

function costMapsFromRecords(
  records: { year: number; system?: string; project_cost_per_unit_usd?: number | null }[] | null
) {
  const byYear = new Map<number, { hvac: number | null; hpwh: number | null }>();
  records?.forEach((record) => {
    if (!byYear.has(record.year)) byYear.set(record.year, { hvac: null, hpwh: null });
    const slot = byYear.get(record.year)!;
    const cost = record.project_cost_per_unit_usd;
    if (cost == null || !Number.isFinite(cost)) return;
    if (record.system === 'HVAC') slot.hvac = cost;
    if (record.system === 'HPWH') slot.hpwh = cost;
  });
  return byYear;
}

export function buildHeatPumpCostFactsFromMaps(params: {
  localByYear: Map<number, { hvac: number | null; hpwh: number | null }>;
  californiaByYear: Map<number, { hvac: number | null; hpwh: number | null }>;
  selectedTechnology: 'HVAC' | 'HPWH';
}): HeatPumpCostFacts {
  const years = [...params.localByYear.keys()].filter((y) => params.californiaByYear.has(y));
  const comparisonYear = years.length ? Math.max(...years) : null;
  if (comparisonYear == null) {
    return {
      comparisonYear: null,
      localHvacProjectCost: null,
      localHpwhProjectCost: null,
      californiaHvacProjectCost: null,
      californiaHpwhProjectCost: null,
      selectedTechnology: params.selectedTechnology,
      localSelectedAverage: null,
      californiaSelectedAverage: null,
    };
  }
  const local = params.localByYear.get(comparisonYear)!;
  const ca = params.californiaByYear.get(comparisonYear)!;
  const localSelectedAverage =
    params.selectedTechnology === 'HVAC' ? local.hvac : local.hpwh;
  const californiaSelectedAverage =
    params.selectedTechnology === 'HVAC' ? ca.hvac : ca.hpwh;

  return {
    comparisonYear,
    localHvacProjectCost: local.hvac,
    localHpwhProjectCost: local.hpwh,
    californiaHvacProjectCost: ca.hvac,
    californiaHpwhProjectCost: ca.hpwh,
    selectedTechnology: params.selectedTechnology,
    localSelectedAverage,
    californiaSelectedAverage,
  };
}

export async function fetchHeatPumpCostFacts(
  geofips: number,
  selectedTechnology: 'HVAC' | 'HPWH' = 'HVAC'
): Promise<HeatPumpCostFacts> {
  const [locationData, californiaData] = await Promise.all([
    fetchHeatPumps({
      select: 'year, system, project_cost_per_unit_usd',
      geofips,
      systems: ['HVAC', 'HPWH'],
    }),
    fetchHeatPumps({
      select: 'year, system, project_cost_per_unit_usd',
      geofips: 6,
      systems: ['HVAC', 'HPWH'],
    }),
  ]);

  if (locationData.error || californiaData.error) {
    return buildHeatPumpCostFactsFromMaps({
      localByYear: new Map(),
      californiaByYear: new Map(),
      selectedTechnology,
    });
  }

  return buildHeatPumpCostFactsFromMaps({
    localByYear: costMapsFromRecords(locationData.data),
    californiaByYear: costMapsFromRecords(californiaData.data),
    selectedTechnology,
  });
}

/** Share KPI only when HVAC+HPWH account for all TECH-recorded units (no other system categories). */
export function heatPumpTechnologyShareEligible(
  hvacUnits: number,
  hpwhUnits: number,
  totalRecordedUnits: number
): boolean {
  if (totalRecordedUnits <= 0) return false;
  const categorized = hvacUnits + hpwhUnits;
  return categorized > 0 && categorized === totalRecordedUnits;
}

export const TECH_PROGRAM_QUALIFIER =
  'TECH Clean California incentive records (participating/incentivized projects—not all heat pumps installed in the community).';
